import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { EditTimeEntryDialog, fromPTInput } from '../../../pages/admin/AdminTimeTrackingPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddMissingTimeDialog = ({ appointmentId, team, headers, onClose, onSaved }) => {
  const [form, setForm] = useState({ cleaner_user_id: team[0]?.id || '', clock_in_at: '', clock_out_at: '', break_minutes: 0, reason: '' });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.cleaner_user_id || !form.clock_in_at || !form.clock_out_at) { toast.error('Choose the Guru and both times'); return; }
    if (form.reason.trim().length < 3) { toast.error('Add a reason for this correction'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/time-entries/office-correction`, {
        appointment_id: appointmentId, cleaner_user_id: form.cleaner_user_id,
        clock_in_at: fromPTInput(form.clock_in_at), clock_out_at: fromPTInput(form.clock_out_at),
        break_minutes: parseInt(form.break_minutes, 10) || 0, reason: form.reason.trim(),
      }, { headers });
      toast.success('Missing time added as an office correction');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add the time'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-missing-time-dialog">
        <DialogHeader>
          <DialogTitle>Add missing time</DialogTitle>
          <DialogDescription>Recorded as an office correction under your name, with the reason. It doesn't change the visit status or create a payout.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Guru</Label>
            <select className="w-full h-9 rounded-md border border-zinc-200 px-2 text-sm" value={form.cleaner_user_id} onChange={(e) => upd('cleaner_user_id', e.target.value)} data-testid="add-time-guru">
              {team.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Clock in (Pacific time)</Label><Input type="datetime-local" value={form.clock_in_at} onChange={(e) => upd('clock_in_at', e.target.value)} data-testid="add-time-in" /></div>
            <div><Label className="text-xs">Clock out (Pacific time)</Label><Input type="datetime-local" value={form.clock_out_at} onChange={(e) => upd('clock_out_at', e.target.value)} data-testid="add-time-out" /></div>
          </div>
          <div><Label className="text-xs">Break (minutes)</Label><Input type="number" min="0" value={form.break_minutes} onChange={(e) => upd('break_minutes', e.target.value)} data-testid="add-time-break" /></div>
          <div><Label className="text-xs">Reason (required)</Label><Input value={form.reason} maxLength={500} onChange={(e) => upd('reason', e.target.value)} placeholder="e.g. Guru's phone died, times confirmed with client" data-testid="add-time-reason" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="add-time-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="add-time-save">{saving ? 'Saving…' : 'Add time'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TimeCorrectionTools = ({ appointmentId, timeEntries, team, headers, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const done = () => { setEditing(null); setAdding(false); onChanged(); };
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="time-correction-tools">
      {timeEntries.map((te) => (
        <Button key={te.id} size="sm" variant="outline" onClick={() => setEditing(te)} data-testid={`correct-time-${te.id}`}>
          {te.clock_out_at ? 'Correct' : 'Record missed clock-out'} · {te.cleaner_name || 'Guru'}
        </Button>
      ))}
      {team.length > 0 && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-missing-time-btn">Add missing time</Button>}
      {editing && <EditTimeEntryDialog entry={editing} headers={headers} onClose={() => setEditing(null)} onSaved={done} />}
      {adding && <AddMissingTimeDialog appointmentId={appointmentId} team={team} headers={headers} onClose={() => setAdding(false)} onSaved={done} />}
    </div>
  );
};
